import { WorkflowWithTriggersAndTags } from "@/generated";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useQueryClient } from "@tanstack/react-query";
import { IMenuEntry } from "../UI/NoxusMenu";
import { WorkflowService } from "@/generated";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { useMutation } from "@tanstack/react-query";
import { SpotToast } from "../SpotToast";
import { useState } from "react";
import { useSpotUser } from "@/utils/spotUser";
import { usePathname, useRouter } from "next/navigation";
import { UseDisclosureReturn } from "@/utils/useDisclosure";
import {
  ArchiveRestore,
  ArrowUpToLine,
  Copy,
  ExternalLink,
  Layers2,
  Pencil,
  Play,
  Trash2,
  ChartBar,
} from "lucide-react";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";

export const useFlowTableContextMenu = ({
  selectedStatus,
  editModalControls,
  setDeleteModalOpen,
  workflow,
  onDuplicate,
  onExport,
}: {
  selectedStatus: {
    label: string;
    value: string;
  };
  editModalControls: UseDisclosureReturn;
  setDeleteModalOpen: (open: boolean) => void;
  workflow: WorkflowWithTriggersAndTags | null;
  onDuplicate?: (workflow: WorkflowWithTriggersAndTags) => void;
  onExport?: (workflow: WorkflowWithTriggersAndTags) => void;
}) => {
  const queryClient = useQueryClient();
  const [isMenuLoading, setIsMenuLoading] = useState(false);
  const { spotGroup, spotUser } = useSpotUser();
  const router = useRouter();
  const pathname = usePathname();

  const restoreWfMutation = useMutation({
    mutationFn: WorkflowService.restoreWorkflow,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS],
      });
    },
    onError: () => {
      SpotToast.error("Error restoring the flow");
    },
    onSettled: () => {
      setIsMenuLoading(false);
    },
  });

  const duplicateWfMutation = useMutation({
    mutationFn: WorkflowService.duplicateWorkflow,
    onSuccess: ({ data: { id } }) => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS],
      });
      const win = window.open(`/flows/${id}`, "_blank");
      win?.focus();
    },
    onError: () => {
      SpotToast.error("Error duplicating the flow");
    },
    onSettled: () => {
      setIsMenuLoading(false);
    },
  });

  const openWorkflow = () => {
    if (workflow?.id) {
      window.open(`/flows/${workflow.id}`, "_blank", "noopener,noreferrer");
    } else {
      SpotToast.error("Could not open the flow. Please try again later.");
    }
  };

  const exportWfMutation = useMutation({
    mutationFn: WorkflowService.exportWorkflow,
    onSuccess: (response) => {
      // Create a blob from the response data
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: "application/json",
      });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString();
      link.download = `${timestamp}-${workflow ? workflow.name.toLowerCase().replace(/\s+/g, "-") : "flow"}-export.nx`;

      // Append to body, click and cleanup
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: () => {
      SpotToast.error("Error exporting the flow");
    },
    onSettled: () => {
      setIsMenuLoading(false);
    },
  });

  const getContextMenuItems = (
    workflow: WorkflowWithTriggersAndTags
  ): IMenuEntry[] => {
    if (selectedStatus.value === "deleted") {
      return [
        {
          label: "Restore",
          icon: ArchiveRestore,
          onClick: () => {
            analytics_track(ANALYTICS_EVENTS.WORKFLOW_RESTORE);
            setIsMenuLoading(true);
            restoreWfMutation.mutate({
              path: {
                group_id: spotGroup.id,
                workflow_id: workflow.id,
              },
            });
          },
        },
      ];
    }

    const items: IMenuEntry[] = [
      {
        label: "Open in New Tab",
        onClick: openWorkflow,
        icon: ExternalLink,
      },
      {
        label: "Run",
        onClick: () => {
          router.push(`/flows/${workflow.id}?openRunner=true`);
        },
        auth: {
          level: "group",
          permissions: [WORKSPACE_PERMISSIONS.RUN],
          requiresAllPermissions: false,
        },
        icon: Play,
      },
      {
        label: "Edit Info",
        onClick: editModalControls.onOpen,
        auth: {
          level: "group",
          permissions: [WORKSPACE_PERMISSIONS.EDIT],
          requiresAllPermissions: false,
        },
        icon: Pencil,
      },
      {
        label: "Analytics",
        onClick: () => {
          router.push(`/flows/${workflow.id}?tab=analytics`);
        },
        auth: {
          level: "group",
          permissions: [WORKSPACE_PERMISSIONS.EDIT],
          requiresAllPermissions: false,
        },
        icon: ChartBar,
      },
      {
        label: "Duplicate",
        onClick: () => {
          analytics_track(ANALYTICS_EVENTS.WORKFLOW_DUPLICATE, {
            page: pathname,
          });
          if (onDuplicate) {
            onDuplicate(workflow);
          } else {
            setIsMenuLoading(true);
            duplicateWfMutation.mutate({
              path: {
                group_id: spotGroup.id,
                workflow_id: workflow.id,
              },
            });
          }
        },
        auth: {
          level: "group",
          permissions: [WORKSPACE_PERMISSIONS.EDIT],
          requiresAllPermissions: false,
        },
        icon: Copy,
      },
      {
        label: "Export",
        onClick: () => {
          if (onExport) {
            onExport(workflow);
          } else {
            setIsMenuLoading(true);
            exportWfMutation.mutate({
              path: {
                group_id: spotGroup.id,
                workflow_id: workflow.id,
              },
            });
          }
        },
        auth: {
          level: "group",
          permissions: [WORKSPACE_PERMISSIONS.EDIT],
          requiresAllPermissions: false,
        },
        icon: ArrowUpToLine,
      },
      {
        label: "Delete",
        onClick: () => {
          setDeleteModalOpen(true);
        },
        auth: {
          level: "group",
          permissions: [WORKSPACE_PERMISSIONS.DELETE],
          requiresAllPermissions: false,
        },
        color: "destructive",
        icon: Trash2,
      },
    ];

    return items;
  };

  return { getContextMenuItems };
};
